import { ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { useOutletContext, useSearchParams } from '@remix-run/react';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CommentType } from '~/interfaces/comment';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Subscription } from '~/interfaces/subscriptions';

import ActionsOptionsDropdown from './ActionsOptionsDropdown';
import CommentDisplay from './CommentDisplay';
import CommentLikeAction from './CommentLikeAction';
import ReplyAction from './ReplyAction';

interface Props {
  comment: CommentType;
  postId: string;
  subscription?: Subscription;
  isMaxDepthLevel?: boolean;
  commentsLocked: boolean;
  showReplyButton: boolean;
  isPremiumCommentsOnly?: boolean;
  onReply?: (newComment: CommentType) => void;
  onDelete?: () => void;
  isOptimisticallyDeleted?: boolean;
}

const IconButtonHelper = ({
  children,
  ref,
  onClick,
  text,
  className,
}: {
  className?: string;
  children?: React.ReactNode;
  ref?: React.RefObject<HTMLButtonElement | null>;
  onClick: () => void;
  text: string | number;
}) => {
  return (
    <button
      type="button"
      ref={ref}
      className={cx(
        className,
        'text-wt-text-on-background-color flex items-center gap-1 text-sm opacity-60 hover:opacity-100',
      )}
      onClick={onClick}
    >
      {children}
      <span className="mt-0 text-xs uppercase">{text}</span>
    </button>
  );
};

const useHighlightComment = (currentCommentId: string) => {
  const [searchParams] = useSearchParams();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [stopHighlighting, setStopHighlighting] = useState(false);
  const isHighlighted = searchParams.get('comment_id') === currentCommentId && !stopHighlighting;

  useEffect(() => {
    if (isHighlighted && !hasScrolled) {
      // Grab element by id
      const element = document.getElementById(currentCommentId);

      // Scroll to element if present
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition - 20;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });

        setTimeout(() => {
          setStopHighlighting(true);
        }, 2000);
      }

      // Prevents scrolling to the same comment multiple times
      setHasScrolled(true);
    }
  }, [isHighlighted, hasScrolled, setHasScrolled]);

  return { isHighlighted };
};

const Comment = ({
  comment,
  postId,
  subscription,
  isMaxDepthLevel = false,
  commentsLocked,
  showReplyButton,
  isPremiumCommentsOnly = false,
  isOptimisticallyDeleted,
  onDelete,
  onReply,
}: Props) => {
  const { t } = useTranslation();
  const { admin } = useOutletContext<OutletContext>();
  const { isHighlighted } = useHighlightComment(comment.id);

  const [showReplyTextArea, setShowReplyTextArea] = useState(false);

  const needsToSetProfileDetails = !subscription?.comment_details_set;
  const isLoggedInAsAdmin = Boolean(admin);

  const showActionsDropdown = !commentsLocked && !needsToSetProfileDetails && !comment.is_moderator_comment;

  const handleDelete = () => {
    onDelete?.();
  };

  const onSuccessfulReply = (newComment: CommentType) => {
    onReply?.(newComment);
    setShowReplyTextArea(false);
  };

  const handleReplyTextArea = () => {
    setShowReplyTextArea(!showReplyTextArea);
  };

  if (isOptimisticallyDeleted) {
    return null;
  }

  return (
    <div key={comment.id} className={cx('transition-all', isHighlighted && 'animate-pulse')} id={comment.id}>
      <div>
        <div className="z-20 flex justify-between">
          <CommentDisplay
            imageUrl={comment.avatar_url || ''}
            name={comment.commenter}
            content={comment.content}
            time={comment.created_at}
            isModerator={comment.is_moderator_comment}
          />
          {(showActionsDropdown || isLoggedInAsAdmin) && (
            <ActionsOptionsDropdown
              comment={comment}
              postId={postId}
              subscription={subscription}
              admin={admin}
              onDelete={handleDelete}
            />
          )}
        </div>

        <div className="ml-7 sm:ml-10">
          <div className="flex items-center gap-3 pt-2">
            <CommentLikeAction postId={postId} comment={comment} subscription={subscription} admin={admin} />

            {!isMaxDepthLevel && !commentsLocked && showReplyButton && (
              <IconButtonHelper
                className="-mt-1"
                text={showReplyTextArea ? `${t('comments.replying.cancel_reply')}` : `${t('comments.replying.reply')}`}
                onClick={handleReplyTextArea}
              >
                <ChatBubbleLeftIcon className="mt-0.5 h-4 w-4" />
              </IconButtonHelper>
            )}
          </div>

          {!commentsLocked && showReplyTextArea && (
            <div className={'mt-2'}>
              <ReplyAction
                comment={comment}
                postId={postId}
                subscription={subscription}
                onSuccessfulReply={onSuccessfulReply}
                isPremiumCommentsOnly={isPremiumCommentsOnly}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Comment;
